import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Order } from '../../types';
// Indian Rupee formatting helper
const formatINR = (amount: number): string => {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
};
interface OrderDetailsScreenProps {
  order: Order;
  onBack: () => void;
}
export function OrderDetailsScreen({
  order,
  onBack
}: OrderDetailsScreenProps) {
  return <div className="pb-24 min-h-screen bg-gray-50 animate-in slide-in-from-right duration-300" data-id="element-1121">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-5 py-6 flex items-center gap-3 sticky top-0 z-10" data-id="element-1122">
        <button onClick={onBack} className="w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-1123">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-1124" />
        </button>
        <h1 className="text-2xl font-bold text-white" data-id="element-1125">Order Details</h1>
      </div>

      <div className="px-5 py-6" data-id="element-1126">
        {/* Store Info */}
        <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm" data-id="element-1127">
          <div className="flex items-center gap-4 mb-4" data-id="element-1128">
            <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden" data-id="element-1129">
              <img src={order.storeImage} alt={order.storeName} className="w-full h-full object-cover" data-id="element-1130" />
            </div>
            <div className="flex-1" data-id="element-1131">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1132">
                {order.storeName}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-1133">{order.date}</p>
            </div>
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-orange-50 text-orange-600" data-id="element-1134">
              Preparing
            </span>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm" data-id="element-1135">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1136">Order Items</h3>
          <div className="space-y-3" data-id="element-1137">
            {order.items.map((item, index) => <div key={index} className="flex justify-between items-center py-2" data-id="element-1138">
                <div className="flex-1" data-id="element-1139">
                  <p className="font-medium text-gray-900" data-id="element-1140">{item.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-1141">Qty: {item.quantity}</p>
                </div>
                <p className="font-bold text-gray-900" data-id="element-1142">
                  {formatINR(item.price * item.quantity)}
                </p>
              </div>)}
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm" data-id="element-1143">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1144">
            Order Summary
          </h3>
          <div className="space-y-3" data-id="element-1145">
            <div className="flex justify-between text-sm" data-id="element-1146">
              <span className="text-gray-600" data-id="element-1147">Subtotal</span>
              <span className="font-medium text-gray-900" data-id="element-1148">
                {formatINR(order.total / 1.05)}
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-1149">
              <span className="text-gray-600" data-id="element-1150">GST (5%)</span>
              <span className="font-medium text-gray-900" data-id="element-1151">
                {formatINR(order.total - order.total / 1.05)}
              </span>
            </div>
            <div className="border-t border-gray-100 pt-3 flex justify-between" data-id="element-1152">
              <span className="text-lg font-bold text-gray-900" data-id="element-1153">Total</span>
              <span className="text-lg font-bold text-[#A020F0]" data-id="element-1154">
                {formatINR(order.total)}
              </span>
            </div>
          </div>
        </div>

        {/* Status Timeline */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5" data-id="element-1155">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1156">Order Status</h3>
          <div className="space-y-4" data-id="element-1157">
            <div className="flex items-center gap-3" data-id="element-1158">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-1159">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" data-id="element-1160">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1161" />
                </svg>
              </div>
              <div data-id="element-1162">
                <p className="font-medium text-gray-900" data-id="element-1163">Order Placed</p>
                <p className="text-xs text-gray-500" data-id="element-1164">{order.date}</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-1165">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 animate-pulse" data-id="element-1166">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" data-id="element-1167">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" data-id="element-1168" />
                </svg>
              </div>
              <div data-id="element-1169">
                <p className="font-medium text-gray-900" data-id="element-1170">
                  Preparing Your Order
                </p>
                <p className="text-xs text-gray-500" data-id="element-1171">Estimated: 15-20 mins</p>
              </div>
            </div>
            <div className="flex items-center gap-3 opacity-40" data-id="element-1172">
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0" data-id="element-1173">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" data-id="element-1174">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" data-id="element-1175" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" data-id="element-1176" />
                </svg>
              </div>
              <div data-id="element-1177">
                <p className="font-medium text-gray-900" data-id="element-1178">Ready for Pickup</p>
                <p className="text-xs text-gray-500" data-id="element-1179">We'll notify you</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}